package org.example.project.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.window
import org.example.project.dashboard.DashboardTheme
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.Node
import org.w3c.dom.events.Event

object AppDropdownAppearance {
    const val Surface = "Surface"
    const val Underline = "Underline"
    const val Field = "Field"
    const val Heading = "Heading"
}

object AppDropdownMenuMode {
    const val Attached = "Attached"
    const val Overlay = "Overlay"
}

private fun firstEnabledIndex(
    options: List<All>,
    selectedValue: String?
): Int {
    val selectedIndex = options.indexOfFirst { it.key == selectedValue && it.aktiv }
    if (selectedIndex >= 0) return selectedIndex
    return options.indexOfFirst { it.aktiv }
}

private object AppDropdownStyles : StyleSheet() {
    val rootClass by style {
        position(Position.Relative)
        width(100.percent)
        minWidth(0.px)
    }

    val trigger by style {
        minHeight(36.px)
        width(100.percent)
        display(DisplayStyle.Flex)
        property("box-sizing", "border-box")
        property("user-select", "none")
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(12.px)
        padding(0.px, 12.px)
        color(Color.white)
        cursor("pointer")
    }

    val triggerSurface by style {
        backgroundColor(Color("rgba(23, 26, 32, 0.38)"))
        borderRadius(8.px)
        border {
            width(1.px)
            style(LineStyle.Solid)
            color(Color("#303645"))
        }
    }

    val triggerUnderline by style {
        backgroundColor(Color.transparent)
        border(0.px)
        property("border-bottom", "1px solid rgba(255,255,255,0.24)")
        borderRadius(0.px)
        padding(0.px)
    }

    val triggerField by style {
        backgroundColor(Color.transparent)
        borderRadius(6.px)
        border(0.px)
        padding(0.px, 10.px)
    }

    val triggerDisabled by style {
        opacity(0.45)
        cursor("default")
    }

    val label by style {
        minWidth(0.px)
        color(Color.white)
        property("white-space", "nowrap")
        property("overflow", "hidden")
        property("text-overflow", "ellipsis")
        textAlign("center")
        property("flex", "1 1 auto")
    }

    val arrow by style {
        color(Color("#d7dde8"))
        fontSize(12.px)
        lineHeight("1")
        fontWeight("700")
        property("flex-shrink", "0")
    }

    val menu by style {
        position(Position.Absolute)
        top(100.percent)
        left(0.px)
        property("right", "auto")
        marginTop(6.px)
        property("z-index", "9999")
        property("background", DashboardTheme.PREMIUM_SURFACE_BG)
        property("backdrop-filter", "blur(18px)")
        property("border", "1px solid ${DashboardTheme.BORDER_LIGHT}")
        borderRadius(10.px)
        property("box-shadow", "0 18px 40px ${DashboardTheme.SURFACE_SHADOW}")
        property("min-width", "100%")
        property("width", "max-content")
        property("max-width", "calc(100vw - 16px)")
        maxHeight(320.px)
        overflowY("auto")
        boxSizing("border-box")
    }

    val option by style {
        minHeight(38.px)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        padding(0.px, 12.px)
        boxSizing("border-box")
        color(Color.white)
        cursor("pointer")
        property("white-space", "nowrap")
        property("overflow", "visible")
        property("max-width", "calc(100vw - 40px)")
    }

    val optionActive by style {
        property("background", "linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.10) 100%)")
        property("box-shadow", "inset 0 0 22px rgba(255, 255, 255, 0.08)")
    }

    val optionHovered by style {
        backgroundColor(Color("rgba(255, 255, 255, 0.08)"))
    }

    val optionDisabled by style {
        opacity(0.45)
        cursor("default")
    }
}

@Composable
fun AppDropdown(
    options: List<All>,
    selectedValue: String?,
    onChange: (String) -> Unit,
    placeholder: String? = null,
    enabled: Boolean = true,
    appearance: String = AppDropdownAppearance.Surface,
    menuMode: String = AppDropdownMenuMode.Attached,
    menuContent: (@Composable (() -> Unit) -> Unit)? = null,
    onOpen: (() -> Unit)? = null,
    triggerIcon: String? = null,
    attrs: (AttrsScope<HTMLDivElement>.() -> Unit)? = null,
    optionTextColor: ((All) -> String?)? = null,
    menuOpenUpward: Boolean = false,
    optionIcon: ((All) -> String?)? = null
) {
    Style(AppDropdownStyles)
    var expanded by remember { mutableStateOf(false) }
    var hoveredValue by remember { mutableStateOf<String?>(null) }
    var highlightedIndex by remember(options, selectedValue) { mutableStateOf(firstEnabledIndex(options, selectedValue)) }
    var rootRef by remember { mutableStateOf<HTMLDivElement?>(null) }
    var overlayLeft by remember { mutableStateOf(0.0) }
    var overlayTop by remember { mutableStateOf(0.0) }
    var overlayWidth by remember { mutableStateOf(0.0) }
    var overlayBottom by remember { mutableStateOf(0.0) }
    var upwardMaxHeight by remember { mutableStateOf(320.0) }

    val selectedOption = options.firstOrNull { it.key == selectedValue }
    val triggerLabel = selectedOption?.label ?: placeholder ?: ""
    val canSelectHighlighted = highlightedIndex in options.indices && options[highlightedIndex].aktiv
    val estimatedMenuHeight = if (menuContent != null) minOf(480.0, window.innerHeight.toDouble() - 32.0) else minOf(320.0, options.size * 38.0 + 12.0)

    fun syncOverlayPosition() {
        if (menuMode != AppDropdownMenuMode.Overlay) return
        val rect = rootRef?.getBoundingClientRect() ?: return
        overlayLeft = rect.left
        overlayWidth = if (menuContent != null) minOf(760.0, window.innerWidth.toDouble() - 16.0).coerceAtLeast(rect.width) else rect.width
        if (menuContent != null) overlayLeft = minOf(rect.left, window.innerWidth.toDouble() - overlayWidth - 8.0).coerceAtLeast(8.0)
        val viewportHeight = window.innerHeight.toDouble()
        overlayBottom = viewportHeight - rect.top + 6.0
        upwardMaxHeight = (rect.top - 14.0).coerceIn(0.0, 320.0)
        val belowSpace = viewportHeight - rect.bottom
        val openUpward = belowSpace < estimatedMenuHeight && rect.top > belowSpace
        overlayTop = if (openUpward) {
            (rect.top - estimatedMenuHeight - 6.0).coerceAtLeast(8.0)
        } else {
            rect.bottom + 6.0
        }
    }

    DisposableEffect(expanded, rootRef) {
        if (!expanded || rootRef == null) return@DisposableEffect onDispose {}

        syncOverlayPosition()

        val listener: (Event) -> Unit = { event ->
            val target = event.target as? Node
            if (target == null || rootRef?.contains(target) != true) {
                expanded = false
                hoveredValue = null
                highlightedIndex = firstEnabledIndex(options, selectedValue)
            }
        }

        document.addEventListener("mousedown", listener)
        document.addEventListener("touchstart", listener)
        val repositionListener: (Event) -> Unit = {
            if (expanded) syncOverlayPosition()
        }
        window.addEventListener("resize", repositionListener)
        document.addEventListener("scroll", repositionListener, true)
        onDispose {
            document.removeEventListener("mousedown", listener)
            document.removeEventListener("touchstart", listener)
            window.removeEventListener("resize", repositionListener)
            document.removeEventListener("scroll", repositionListener, true)
        }
    }

    Div({
        classes(AppDropdownStyles.rootClass)
        if (appearance == AppDropdownAppearance.Heading) style {
            width(100.percent)
            fontSize(20.px)
            fontWeight("900")
        }
        ref {
            rootRef = it
            onDispose {
                if (rootRef == it) rootRef = null
            }
        }
    }) {
        Div({
            classes(AppDropdownStyles.trigger)
            when (appearance) {
                AppDropdownAppearance.Heading -> style {
                    backgroundColor(Color.transparent)
                    border(0.px)
                    padding(0.px)
                    minHeight(0.px)
                    justifyContent(JustifyContent.FlexStart)
                    gap(10.px)
                }
                AppDropdownAppearance.Underline -> classes(AppDropdownStyles.triggerUnderline)
                AppDropdownAppearance.Field -> classes(AppDropdownStyles.triggerField)
                else -> classes(AppDropdownStyles.triggerSurface)
            }
            if (!enabled) classes(AppDropdownStyles.triggerDisabled)
            tabIndex(if (enabled) 0 else -1)
            attr("role", "button")
            attr("aria-haspopup", if (onOpen == null) "listbox" else "dialog")
            attr("aria-expanded", expanded.toString())
            if (attrs != null) attrs()
            onClick {
                if (!enabled) return@onClick
                if (onOpen != null) { onOpen(); return@onClick }
                expanded = !expanded
                if (expanded) highlightedIndex = firstEnabledIndex(options, selectedValue)
            }
            onKeyDown { event ->
                if (!enabled) return@onKeyDown
                if (onOpen != null && event.key in listOf("Enter", " ", "ArrowDown", "ArrowUp")) {
                    event.preventDefault(); onOpen(); return@onKeyDown
                }
                if (event.key == "Enter" || event.key == " ") {
                    event.preventDefault()
                    if (expanded && canSelectHighlighted) {
                        onChange(options[highlightedIndex].key)
                        expanded = false
                        hoveredValue = null
                        highlightedIndex = firstEnabledIndex(options, selectedValue)
                    } else {
                        expanded = !expanded
                        if (expanded) highlightedIndex = firstEnabledIndex(options, selectedValue)
                    }
                } else if (event.key == "ArrowDown") {
                    event.preventDefault()
                    if (!expanded) {
                        expanded = true
                        highlightedIndex = firstEnabledIndex(options, selectedValue)
                    } else {
                        val enabledIndices = options.indices.filter { options[it].aktiv }
                        if (enabledIndices.isNotEmpty()) {
                            val currentPos = enabledIndices.indexOf(highlightedIndex).let { if (it >= 0) it else -1 }
                            val nextPos = if (currentPos + 1 >= enabledIndices.size) 0 else currentPos + 1
                            highlightedIndex = enabledIndices[nextPos]
                        }
                    }
                } else if (event.key == "ArrowUp") {
                    event.preventDefault()
                    if (!expanded) {
                        expanded = true
                        highlightedIndex = firstEnabledIndex(options, selectedValue)
                    } else {
                        val enabledIndices = options.indices.filter { options[it].aktiv }
                        if (enabledIndices.isNotEmpty()) {
                            val currentPos = enabledIndices.indexOf(highlightedIndex).let { if (it >= 0) it else 0 }
                            val nextPos = if (currentPos - 1 < 0) enabledIndices.lastIndex else currentPos - 1
                            highlightedIndex = enabledIndices[nextPos]
                        }
                    }
                } else if (event.key == "Escape") {
                    expanded = false
                    hoveredValue = null
                    highlightedIndex = firstEnabledIndex(options, selectedValue)
                } else if (event.key == "Tab") {
                    expanded = false
                    hoveredValue = null
                }
            }
        }) {
            if (triggerIcon != null) Img(src = "images/$triggerIcon.svg", attrs = {
                attr("alt", ""); attr("aria-hidden", "true")
                style { width(18.px); height(18.px); property("filter", "brightness(0) invert(1)") }
            }) else {
                selectedOption?.let { option -> optionIcon?.invoke(option)?.let { icon ->
                    Img(src = "images/$icon.svg", attrs = {
                        attr("alt", ""); attr("aria-hidden", "true")
                        style { width(18.px); height(18.px); flexShrink(0); property("filter", UISettings.STATE_BUTTON_ICON_FILTER) }
                    })
                } }
                Span({ classes(AppDropdownStyles.label); selectedOption?.let { option -> optionTextColor?.invoke(option)?.let { tint -> style { color(Color(tint)) } } } }) { Text(triggerLabel) }
                Span({ classes(AppDropdownStyles.arrow) }) { Text(if (expanded) "^" else "v") }
            }
        }

        if (expanded) {
            Div({
                classes(AppDropdownStyles.menu)
                attr("role", if (menuContent == null) "listbox" else "dialog")
                if (appearance == AppDropdownAppearance.Heading) style {
                    backgroundColor(Color("#171a20"))
                    property("background-image", "none")
                    fontSize(20.px)
                    fontWeight("900")
                }
                if (menuMode == AppDropdownMenuMode.Overlay) {
                    style {
                        position(Position.Fixed)
                        left(overlayLeft.px)
                        top(overlayTop.px)
                        if (menuOpenUpward) {
                            property("top", "auto")
                            bottom(overlayBottom.px)
                            marginTop(0.px)
                            maxHeight(upwardMaxHeight.px)
                        }
                        property("min-width", "${overlayWidth}px")
                        property("width", if (menuContent == null) "max-content" else "${overlayWidth}px")
                        if (menuContent != null) { height(estimatedMenuHeight.px); maxHeight(estimatedMenuHeight.px); overflow("hidden") }
                        property("max-width", "calc(100vw - 16px)")
                    }
                }
            }) {
                if (menuContent != null) {
                    menuContent { expanded = false; hoveredValue = null }
                } else {
                options.forEachIndexed { index, option ->
                    val active = option.key == selectedValue
                    val hovered = option.key == hoveredValue || index == highlightedIndex

                    Div({
                        classes(AppDropdownStyles.option)
                        if (active) classes(AppDropdownStyles.optionActive)
                        if (hovered) classes(AppDropdownStyles.optionHovered)
                        if (!option.aktiv) classes(AppDropdownStyles.optionDisabled)
                        optionTextColor?.invoke(option)?.let { tint -> style { color(Color(tint)) } }
                        attr("role", "option")
                        attr("aria-selected", active.toString())
                        onMouseEnter {
                            hoveredValue = option.key
                            highlightedIndex = index
                        }
                        onMouseLeave {
                            if (hoveredValue == option.key) hoveredValue = null
                        }
                        onClick {
                            if (!option.aktiv) return@onClick
                            onChange(option.key)
                            expanded = false
                            hoveredValue = null
                            highlightedIndex = firstEnabledIndex(options, selectedValue)
                        }
                    }) {
                        optionIcon?.invoke(option)?.let { icon ->
                            Img(src = "images/$icon.svg", attrs = {
                                attr("alt", ""); attr("aria-hidden", "true")
                                style { width(18.px); height(18.px); marginRight(8.px); flexShrink(0); property("filter", UISettings.STATE_BUTTON_ICON_FILTER) }
                            })
                        }
                        Text(option.label)
                    }
                }
                }
            }
        }
    }
}
